import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, X, Users } from "lucide-react";
import OfferFireworks from "./offers/OfferFireworks";
import { usePrefersReducedMotion } from "../hooks/use-reduced-motion";
import { resolveOfferTheme, getOfferLabels, formatMonths } from "../lib/offers/offerTheme";

const ambientColors = ["#ef4444", "#f59e0b", "#22c55e", "#06b6d4", "#8b5cf6"];
const burstShapes = [
	{ id: "dot-left", type: "dot", size: 17, color: "#22b6e6", left: "-16px", top: "42px" },
	{ id: "plus-right", type: "plus", size: 18, color: "#f59e0b", right: "-18px", top: "18px" },
	{ id: "ring-bottom-right", type: "ring", size: 12, color: "#2563eb", right: "6px", bottom: "6px" },
	{ id: "mini-dot-bottom", type: "dot", size: 8, color: "#fb923c", left: "50%", bottom: "-10px", translateX: "-50%" },
	{ id: "zigzag-left", type: "zigzag", size: 20, color: "#ef5a4d", left: "-18px", bottom: "4px" },
	{ id: "bar-top", type: "bar", size: 14, color: "#2563eb", left: "50%", top: "-18px", translateX: "-50%" },
];

const createAmbientPieces = (count = 72) =>
	Array.from({ length: count }, (_, index) => {
		const size = 5 + Math.random() * 9;

		return {
			id: `${index}-${Math.random().toString(16).slice(2)}`,
			left: `${Math.random() * 100}%`,
			top: `${-20 - Math.random() * 80}px`,
			delay: `${Math.random() * 2.2}s`,
			duration: `${4.2 + Math.random() * 3.2}s`,
			size,
			drift: `${(Math.random() - 0.5) * 120}px`,
			rotate: `${Math.random() * 360}deg`,
			color: ambientColors[index % ambientColors.length],
			shape: index % 3 === 0 ? "circle" : "rect",
		};
	});

const formatAmount = (amount) => {
	const numericAmount = Number(amount || 0);
	return numericAmount.toLocaleString("en-IN");
};

const formatAccessName = (accessName) => {
	if (!accessName) return "";
	
	const accessMap = {
		BUILDER_PROPERTY: "Builder Property",
		OWNER_PROPERTY: "Owner Property",
		BROKER_PROPERTY: "Broker Property",
	};
	
	if (accessMap[accessName]) {
		return accessMap[accessName];
	}
	
	// Fallback: convert UPPER_CASE_WITH_UNDERSCORE to Title Case With Spaces
	return accessName
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

const formatLongDate = (value) => {
	const date = new Date(value);
	if (!Number.isFinite(date.getTime())) return "";
	return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// The offer the server actually applied, or null (no offer = today's screen).
const getAppliedOffer = (summary) =>
	summary?.appliedOffer && Number(summary.appliedOffer.bonusMonths) > 0 ? summary.appliedOffer : null;

const getDefaultInfoCards = (summary) => {
	const planName = summary.planName || "Premium Plan";
	const appliedOffer = getAppliedOffer(summary);
	const durationInMonth = Number(appliedOffer?.totalMonths || summary.durationInMonth || 0);
	const amount = Number(summary.amount || 0);
	const includedStaff = Number(summary.includedStaff || 0);
	const extraStaff = Number(summary.extraStaff || 0);
	const totalStaff = Number(summary.totalStaff || includedStaff + extraStaff);

	return [
		{
			label: "Amount Paid",
			value: `₹${formatAmount(amount)}`,
		},
		{
			label: "Plan",
			value: planName,
			meta: durationInMonth ? `${durationInMonth} month${durationInMonth > 1 ? "s" : ""}` : "Activated",
		},
		{
			label: "Staff Limit",
			value: String(totalStaff),
			meta: extraStaff > 0 ? `${extraStaff} extra added` : `${includedStaff} included`,
		},
	];
};

const PaymentCelebration = ({ isOpen, summary = {}, onClose }) => {
	const pieces = useMemo(() => createAmbientPieces(), [isOpen]);
	const [showSuccessBurst, setShowSuccessBurst] = useState(false);
	const prefersReducedMotion = usePrefersReducedMotion();
	const appliedOffer = getAppliedOffer(summary);
	const offerTheme = useMemo(() => resolveOfferTheme(summary.offerDisplay || {}), [summary.offerDisplay]);
	const fireworkColors = useMemo(
		() => [offerTheme.accent, offerTheme.accentSoft, offerTheme.primary],
		[offerTheme]
	);

	useEffect(() => {
		if (!isOpen) return undefined;

		setShowSuccessBurst(false);
		const burstTimer = window.setTimeout(() => {
			setShowSuccessBurst(true);
		}, 1000);

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		const handleKeyDown = (event) => {
			if (event.key === "Escape" && typeof onClose === "function") {
				onClose();
			}
		};

		window.addEventListener("keydown", handleKeyDown);

		return () => {
			window.clearTimeout(burstTimer);
			document.body.style.overflow = previousOverflow;
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const accessItems = Array.isArray(summary.accessItems) ? summary.accessItems.filter(Boolean) : [];
	const addonItems = Array.isArray(summary.addonItems) ? summary.addonItems.filter(Boolean) : [];
	const combinedAccess = [...accessItems, ...addonItems];
	const offerBonusMonths = Number(appliedOffer?.bonusMonths || 0);
	const offerTitle = appliedOffer
		? `${getOfferLabels(offerTheme).greeting} ${offerTheme.icon} Your bonus month${offerBonusMonths === 1 ? " is" : "s are"} added`
		: "";
	const title = summary.title || offerTitle || "Your plan is active";
	const description =
		summary.description ||
		"The payment was processed successfully and your account has been updated with the selected plan and access.";
	const offerExpiryText = appliedOffer ? formatLongDate(summary.expiredDate) : "";
	// No ambient confetti for reduced motion on the offer screen; the no-offer
	// screen stays exactly as it was.
	const showAmbientPieces = !(appliedOffer && prefersReducedMotion);
	const infoCards = Array.isArray(summary.infoCards) && summary.infoCards.length > 0
		? summary.infoCards
		: getDefaultInfoCards(summary);

	return (
		<div
			className="fixed inset-0 z-[220] flex items-start justify-center bg-slate-950/70 px-4 py-6 pt-8 backdrop-blur-md sm:pt-12"
			role="dialog"
			aria-modal="true"
			aria-labelledby="payment-success-title"
			onClick={(event) => {
				if (event.target === event.currentTarget && typeof onClose === "function") {
					onClose();
				}
			}}
		>
			<style>{`
				@keyframes pb-celebrate-fall {
					0% {
						transform: translate3d(0, -12vh, 0) rotate(var(--piece-rotate));
						opacity: 0;
					}
					12% {
						opacity: 0.95;
					}
					100% {
						transform: translate3d(var(--piece-drift), 122vh, 0) rotate(calc(var(--piece-rotate) + 520deg));
						opacity: 0.05;
					}
				}

				@keyframes pb-celebrate-pop {
					0% {
						transform: scale(0.86);
						opacity: 0;
					}
					100% {
						transform: scale(1);
						opacity: 1;
					}
				}

				@keyframes pb-check-draw {
					to {
						stroke-dashoffset: 0;
					}
				}

				@keyframes pb-ring-rotate {
					to {
						transform: rotate(360deg);
					}
				}

				@keyframes pb-burst-pop {
					0% {
						opacity: 0;
						transform: scale(0.82);
					}
					100% {
						opacity: 1;
						transform: scale(1);
					}
				}

				@keyframes pb-shape-in {
					0% {
						opacity: 0;
						transform: translateY(6px) scale(0.84);
					}
					100% {
						opacity: 1;
						transform: translateY(0) scale(1);
					}
				}
			`}</style>

			<div className="pointer-events-none absolute inset-0 overflow-hidden">
				<div className="absolute left-[-10%] top-1/4 h-72 w-72 rounded-full bg-emerald-400/20 blur-3xl" />
				<div className="absolute right-[-12%] top-1/3 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
				{showAmbientPieces && pieces.map((piece) => (
					<span
						key={piece.id}
						className="absolute"
						style={{
							left: piece.left,
							top: piece.top,
							width: piece.size,
							height: piece.shape === "circle" ? piece.size : piece.size * 0.62,
							borderRadius: piece.shape === "circle" ? "9999px" : "3px",
							backgroundColor: piece.color,
							opacity: 0.9,
							boxShadow: "0 0 10px rgba(255,255,255,0.16)",
							animation: `pb-celebrate-fall ${piece.duration} linear ${piece.delay} infinite`,
							["--piece-drift"]: piece.drift,
							["--piece-rotate"]: piece.rotate,
						}}
					/>
				))}
			</div>

			<div className="relative w-full max-w-[33rem] overflow-hidden rounded-[1.75rem] border border-white/30 bg-white shadow-[0_30px_120px_rgba(15,23,42,0.38)] animate-[pb-celebrate-pop_.28s_ease-out] sm:max-w-[35rem]">
				{appliedOffer ? (
					<>
						<div className="h-2" style={{ background: `linear-gradient(90deg, ${offerTheme.secondary}, ${offerTheme.primary}, ${offerTheme.accent})` }} />
						<OfferFireworks colors={fireworkColors} />
					</>
				) : (
					<div className="h-2 bg-gradient-to-r from-emerald-500 via-cyan-500 to-amber-400" />
				)}

				<button
					type="button"
					onClick={onClose}
					className="absolute right-4 top-4 rounded-full bg-slate-100 p-2 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-900"
					aria-label="Close payment celebration"
				>
					<X size={18} />
				</button>

				<div className="px-5 py-4 sm:px-6 sm:py-5">
					<div className="relative mx-auto mb-1 h-[7.5rem] w-[7.5rem]">
						{!showSuccessBurst ? (
							<div className="absolute inset-0 flex items-center justify-center">
								<svg
									viewBox="0 0 160 160"
									className="h-full w-full"
									aria-hidden="true"
									style={{ animation: "pb-ring-rotate 2.2s linear infinite" }}
								>
									<circle cx="80" cy="80" r="58" fill="none" stroke="#3a9b00" strokeWidth="12" strokeLinecap="round" strokeDasharray="280 120" />
									<circle cx="80" cy="80" r="40" fill="none" stroke="#3a9b00" strokeWidth="12" strokeLinecap="round" strokeDasharray="190 95" transform="rotate(35 80 80)" />
									<circle cx="80" cy="80" r="22" fill="none" stroke="#3a9b00" strokeWidth="12" strokeLinecap="round" strokeDasharray="106 58" transform="rotate(70 80 80)" />
								</svg>
							</div>
						) : (
							<div className="absolute inset-0" style={{ animation: "pb-burst-pop .42s ease-out" }}>
								{burstShapes.map((shape, index) => (
									<span
										key={shape.id}
										className="absolute"
										style={{
											left: shape.left,
											right: shape.right,
											top: shape.top,
											bottom: shape.bottom,
											transform: shape.translateX ? `translateX(${shape.translateX})` : undefined,
											width: shape.size,
											height: shape.size,
											color: shape.color,
											animation: `pb-shape-in .4s ease ${index * 0.06}s both`,
										}}
									>
										{shape.type === "dot" && <span className="block h-full w-full rounded-full" style={{ backgroundColor: shape.color }} />}
										{shape.type === "ring" && <span className="block h-full w-full rounded-full border-[5px]" style={{ borderColor: shape.color }} />}
										{shape.type === "plus" && (
											<span className="relative block h-full w-full rotate-12">
												<span className="absolute left-1/2 top-0 h-full w-[28%] -translate-x-1/2 rounded-sm" style={{ backgroundColor: shape.color }} />
												<span className="absolute left-0 top-1/2 h-[28%] w-full -translate-y-1/2 rounded-sm" style={{ backgroundColor: shape.color }} />
											</span>
										)}
										{shape.type === "bar" && <span className="block h-full w-[26%] rounded-sm mx-auto" style={{ backgroundColor: shape.color }} />}
										{shape.type === "zigzag" && (
											<svg viewBox="0 0 20 20" className="h-full w-full" fill="none">
												<path d="M2 15.5h6.2l2-5H18" stroke={shape.color} strokeWidth="4.2" strokeLinecap="round" strokeLinejoin="round" />
											</svg>
										)}
									</span>
								))}

								<div className="absolute inset-0 flex items-center justify-center">
									<div className="flex h-24 w-24 items-center justify-center rounded-full bg-[#3a9b00]">
										<svg viewBox="0 0 52 52" className="h-14 w-14 text-white" fill="none" aria-hidden="true">
											<path
												d="M13 27.5L22 36L39.5 19"
												stroke="currentColor"
												strokeWidth="6"
												strokeLinecap="round"
												strokeLinejoin="round"
												style={{
													strokeDasharray: 60,
													strokeDashoffset: 60,
													animation: "pb-check-draw .5s ease forwards .14s",
												}}
											/>
										</svg>
									</div>
								</div>
							</div>
						)}
					</div>

					<div className="mt-3 text-center">
						<div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-1 text-xs font-bold uppercase tracking-[0.3em] text-emerald-700">
							<Sparkles size={14} />
							Payment successful
						</div>
						<h2 id="payment-success-title" className="mt-2 text-[1.7rem] font-black text-slate-900 sm:text-[2rem]">
							{title}
						</h2>
						{appliedOffer ? (
							<div
								className="mx-auto mt-3 max-w-md rounded-2xl px-4 py-3 text-left"
								style={{ background: offerTheme.background, color: offerTheme.text }}
							>
								<p className="text-sm font-bold sm:text-base">
									{formatMonths(appliedOffer.paidMonths)} paid + {formatMonths(offerBonusMonths)} free ={" "}
									{formatMonths(appliedOffer.totalMonths)}
								</p>
								{summary.isPending && (
									<p className="mt-1 text-sm" style={{ color: offerTheme.mutedText }}>
										Starts after your current plan ends
									</p>
								)}
								{offerExpiryText && (
									<p className="mt-1 text-sm" style={{ color: offerTheme.mutedText }}>
										Active till {offerExpiryText}
									</p>
								)}
							</div>
						) : (
							<p className="mt-1.5 text-sm leading-5 text-slate-500">
								{description}
							</p>
						)}
					</div>

					<div className="mt-4 grid gap-2.5 sm:grid-cols-3">
						{infoCards.map((card, index) => (
							<div key={`${card.label || "card"}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
								<div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-400">{card.label}</div>
								<div className="mt-1.5 text-[0.95rem] font-black text-slate-900 sm:text-lg">{card.value}</div>
								{card.meta ? (
									<div className="mt-0.5 text-xs font-semibold text-slate-500">
										{card.meta}
									</div>
								) : null}
							</div>
						))}
					</div>

					<div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-3.5">
						<div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-slate-400">
							<Users size={14} />
							Access Granted
						</div>
						<div className="mt-3 flex flex-wrap gap-2">
							{combinedAccess.length > 0 ? (
								Array.from(new Set(combinedAccess.map(item => formatAccessName(item)))).map((uniqueItem) => (
									<span
										key={uniqueItem}
										className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700"
									>
										{uniqueItem}
									</span>
								))
							) : (
								<span className="text-sm font-medium text-slate-500">No extra access items were selected.</span>
							)}
						</div>
					</div>

					<div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
						<button
							type="button"
							onClick={onClose}
							className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-100"
						>
							Close
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PaymentCelebration;
